"use client";

/**
 * P4-05 · Cross-sell slot (mini-cart + full cart).
 *
 * Reads `cart.crossSells` for the current surface and renders compact,
 * one-tap-add cards. Self-gating: the procedure returns `[]` when the brand's
 * `crossSells` flag is off, the cart is empty, or nothing relevant is left, so
 * the slot renders nothing without any prop plumbing. Adding runs through the
 * shared cart path (server-authoritative stock); on success we invalidate the
 * list so the just-added item drops out and fresh picks fill in.
 *
 * `surface` drives both the query's per-surface cap and the layout: the
 * mini-cart is a narrow stacked list, the full cart a responsive grid.
 */
import * as React from "react";

import { useToast } from "~/components/ui/toast";
import { useCart } from "~/lib/cart/use-cart";
import { formatMoney } from "~/lib/money";
import { fallBackToOriginal, thumbnailImage } from "~/lib/image-loader";
import { cn } from "~/lib/utils";
import { api } from "~/trpc/react";
import type { CrossSellItem } from "~/server/catalog/cross-sells";
import { useTranslator } from "~/i18n/provider";

export function CrossSellSlot({
  surface,
  enabled = true,
  title,
}: {
  surface: "mini" | "full";
  /** Skip the query entirely (e.g. brand flag off, or cart empty). */
  enabled?: boolean;
  title?: string;
}) {
  const t = useTranslator();
  const heading = title ?? t("cart.youMightAlsoLike");
  const query = api.cart.crossSells.useQuery(
    { surface },
    { enabled, staleTime: 60_000 },
  );
  const items = query.data ?? [];
  if (!enabled || items.length === 0) return null;

  return (
    <section
      aria-label={heading}
      data-testid={`cart-cross-sell-${surface}`}
      className={cn(
        // Mini: sits at the foot of the scroll list; `-mx-4` lets the divider
        // span the drawer's padded gutter edge-to-edge.
        surface === "mini"
          ? "-mx-4 mt-3 border-t border-border px-4 pt-4 pb-2"
          : "mt-8",
      )}
    >
      <h2
        className={cn(
          "font-heading font-semibold text-fg",
          surface === "mini" ? "mb-3 text-sm" : "mb-4 text-lg",
        )}
      >
        {heading}
      </h2>
      <ul
        className={
          surface === "mini"
            ? "flex flex-col gap-2"
            : "grid grid-cols-2 gap-3 sm:grid-cols-3"
        }
      >
        {items.map((item) => (
          <CrossSellCard key={item.productId} item={item} surface={surface} />
        ))}
      </ul>
    </section>
  );
}

function CrossSellCard({
  item,
  surface,
}: {
  item: CrossSellItem;
  surface: "mini" | "full";
}) {
  const { addItem } = useCart();
  const utils = api.useUtils();
  const { toast } = useToast();
  const [adding, setAdding] = React.useState(false);

  const add = async () => {
    setAdding(true);
    try {
      await addItem({
        variantId: item.variantId,
        quantity: 1,
        personalisation: null,
      });
      toast({ title: `Added ${item.title} to cart`, tone: "success" });
      // The item is now in the cart — refresh the list so it drops out.
      void utils.cart.crossSells.invalidate();
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : "Couldn't add that item",
        tone: "danger",
      });
    } finally {
      setAdding(false);
    }
  };

  const onSale =
    item.compareAtMinor != null && item.compareAtMinor > item.priceMinor;

  // Mini-cart: a horizontal row (thumb · title/price · add). Full cart: a card.
  if (surface === "mini") {
    return (
      <li className="flex items-center gap-3 rounded-[var(--btn-radius)] border border-border p-2">
        <Thumb item={item} cssWidth={48} className="size-12" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-fg">{item.title}</p>
          <Price item={item} onSale={onSale} />
        </div>
        <AddButton adding={adding} onClick={add} item={item} compact />
      </li>
    );
  }

  return (
    <li className="flex flex-col rounded-[var(--btn-radius)] border border-border p-2">
      {/* A grid card: 2-up on phones, 3-up from sm; never wider than ~240 px. */}
      <Thumb item={item} cssWidth={240} className="mb-2 aspect-square w-full" rounded />
      <p className="mb-0.5 line-clamp-2 min-h-[2.5rem] text-sm text-fg">
        {item.title}
      </p>
      <Price item={item} onSale={onSale} className="mb-2" />
      <AddButton adding={adding} onClick={add} item={item} />
    </li>
  );
}

function Thumb({
  item,
  cssWidth,
  className,
  rounded = false,
}: {
  item: CrossSellItem;
  /** The box's largest CSS width, for the resized source. */
  cssWidth: number;
  className?: string;
  rounded?: boolean;
}) {
  const radius = rounded ? "rounded-md" : "rounded";
  if (!item.imageUrl) {
    return (
      <div
        aria-hidden
        className={cn("shrink-0 bg-surface-2", radius, className)}
      />
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      {...thumbnailImage(item.imageUrl, cssWidth)}
      onError={fallBackToOriginal(item.imageUrl)}
      alt={item.imageAlt}
      className={cn("shrink-0 object-cover", radius, className)}
    />
  );
}

function Price({
  item,
  onSale,
  className,
}: {
  item: CrossSellItem;
  onSale: boolean;
  className?: string;
}) {
  return (
    <p className={cn("flex items-baseline gap-1.5 text-sm", className)}>
      <span
        className={cn(
          "font-medium tabular-nums",
          onSale ? "text-danger" : "text-fg",
        )}
      >
        {formatMoney(item.priceMinor, item.currency)}
      </span>
      {onSale ? (
        <span className="text-xs text-fg-muted line-through tabular-nums">
          {formatMoney(item.compareAtMinor!, item.currency)}
        </span>
      ) : null}
    </p>
  );
}

function AddButton({
  adding,
  onClick,
  item,
  compact = false,
}: {
  adding: boolean;
  onClick: () => void;
  item: CrossSellItem;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={adding}
      aria-busy={adding}
      aria-label={`Add ${item.title} to cart`}
      data-testid={`cart-cross-sell-add-${item.slug}`}
      className={cn(
        "shrink-0 rounded-[var(--btn-radius)] border border-border font-medium text-fg transition-colors hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]",
        compact ? "px-3 py-1.5 text-xs" : "w-full px-3 py-2 text-sm",
      )}
    >
      {adding ? "Adding…" : "Add"}
    </button>
  );
}
